import type { JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useDashboardPropertyDetail } from '@/hooks/useDashboardProperties';
import { PropertyEditForm } from '@/routes/dashboard/propertyEdit/PropertyEditForm';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';

export function DashboardPropertyEdit(): JSX.Element {
  const user = useDashboardUser();
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const normalizedPropertyId = (propertyId ?? '').trim();

  const detailQuery = useDashboardPropertyDetail({
    user,
    propertyId: normalizedPropertyId.length > 0 ? normalizedPropertyId : null,
  });

  const backToProperty = () => {
    navigate(`/dashboard/properties/${normalizedPropertyId}`);
  };

  const skeleton = (
    <div className="bg-surface-muted h-28 animate-pulse rounded-3xl border border-surface-subtle" />
  );

  return (
    <section className="flex flex-col gap-xl py-8">
      <button
        type="button"
        onClick={backToProperty}
        className="min-h-11 w-fit rounded-full border border-brand-primary/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      >
        Back to property
      </button>

      <AsyncState
        data={detailQuery.data ?? null}
        isLoading={detailQuery.isLoading || detailQuery.isFetching}
        error={detailQuery.error}
        skeleton={skeleton}
      >
        {(detail) => {
          const propertyLabel = formatPropertyAddress(detail.property.address);
          const resolvedPropertyId =
            typeof detail.property.id === 'string' ? detail.property.id.trim() : '';

          if (resolvedPropertyId.length === 0) {
            return (
              <div className="rounded-3xl border border-surface-subtle bg-white p-6 text-sm text-text-primary/80 shadow-sm">
                <p className="text-base font-semibold text-text-primary">Property not found</p>
                <p className="mt-2">
                  The requested property could not be located. Return to the properties list and try
                  again.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <PillButton
                    onClick={() => {
                      navigate('/dashboard/properties');
                    }}
                  >
                    Back to properties
                  </PillButton>
                </div>
              </div>
            );
          }

          return (
            <>
              <header className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-primary/70">
                  Edit property
                </p>
                <h1 className="text-3xl font-semibold text-text-primary">{propertyLabel}</h1>
                <p className="text-sm text-text-primary/70">Update property details.</p>
              </header>

              <div className="rounded-3xl border border-surface-subtle bg-white p-6 shadow-sm">
                <PropertyEditForm
                  propertyId={resolvedPropertyId}
                  property={detail.property}
                  onCancel={backToProperty}
                  onSaved={backToProperty}
                />
              </div>
            </>
          );
        }}
      </AsyncState>
    </section>
  );
}
